import type { PickerSection } from '@/components/EmojiPickerSheet';

/**
 * reaction-emoji-signal — embedded copy of the qualified reaction catalogue.
 *
 * The SERVER owns this list (`GET /reactions`): with a family and a signed score
 * per emoji, a duplicated list in two repos would be a measurement reference in
 * two copies, and a divergence on 🥶 would be silent — the picker filing it
 * under "energy" while the analytics count it as "calm".
 *
 * This copy exists for ONE reason: Capacitor. The picker must open instantly and
 * offline, so it renders from here and refreshes from the server in the
 * background. A divergence therefore shows up as a 400 at tap time — loud, not
 * silent, which is the trade the design accepted.
 *
 * Generated from racore-back/database/seeds/reaction-catalogue.ts. Emojis only:
 * the scores never reach the client, they are what the backend analyses.
 */
export const REACTION_FAMILIES: { id: string; icon: string; emojis: string[] }[] = [
  {
    id: 'energy',
    icon: '🔥',
    emojis: [
      '🔥',
      '⚡',
      '🤯',
      '💯',
      '🚀',
      '🤘',
      '💥',
      '🥶',
      '🥵',
      '🧨',
      '🎆',
      '🌋',
      '☄️',
      '💪',
      '🦾',
      '😤',
      '⭐',
      '🌟',
      '✨',
      '💫',
    ],
  },
  {
    id: 'crush',
    icon: '💜',
    emojis: [
      '❤️',
      '❤️‍🔥',
      '💖',
      '💘',
      '😍',
      '🥰',
      '😻',
      '🫶',
      '😘',
      '💗',
      '💓',
      '💞',
      '💕',
      '💟',
      '💌',
      '🧡',
      '💛',
      '💚',
      '💙',
      '💜',
      '🤎',
      '🖤',
      '🤍',
      '🌹',
      '💐',
    ],
  },
  {
    id: 'dance',
    icon: '🕺',
    emojis: ['🕺', '💃', '🪩', '🥳', '👯', '🎉', '🎊', '🍾', '🥂', '🍻', '🤸'],
  },
  { id: 'emotion', icon: '🥹', emojis: ['😭', '🥹', '😢', '🥲', '🫂', '🕊️', '🥺'] },
  { id: 'calm', icon: '😌', emojis: ['😌', '🧘', '🌙', '☕', '🍃', '🌿', '🌸', '🌅', '🌄'] },
  {
    id: 'respect',
    icon: '😎',
    emojis: [
      '😎',
      '👑',
      '🐐',
      '🏆',
      '🥇',
      '💎',
      '👏',
      '🙌',
      '🔝',
      '🫡',
      '🤝',
      '🧠',
      '🎩',
      '🆒',
      '🥈',
      '🥉',
      '🎖️',
      '🏅',
      '👍',
    ],
  },
  { id: 'fun', icon: '😂', emojis: ['😂', '🤣', '😹', '🤪', '😆', '😜', '🙃', '🤭'] },
  { id: 'surprise', icon: '👀', emojis: ['👀', '😲', '🙀', '😳', '🧐', '🤨', '🫣'] },
  // The two negative families. Reachable from the jump strip, or the pole we
  // just reopened would stay buried and never get used.
  { id: 'boredom', icon: '🥱', emojis: ['🥱', '💤', '🤷', '😐', '😑', '🫥'] },
  { id: 'disappointment', icon: '🫤', emojis: ['🫤', '🙄', '👎'] },
];

/** Build picker sections, labels resolved by the caller. */
export function reactionSections(label: (familyId: string) => string): PickerSection[] {
  return REACTION_FAMILIES.map((f) => ({
    id: f.id,
    icon: f.icon,
    label: label(f.id),
    emojis: f.emojis,
  }));
}
